/**
 * Melody visualizations: they show the pitch of the lead line (and the notes sounding) in colour.
 * Pitch comes from dsp/pitch.ts; colour from noteColour() (the 12 notes round the rainbow, C red)
 * or, if chosen, from the palette.
 */
import type { ParamDef } from '../../../shared/params';
import type { VisualizationDef } from '../../../shared/viz';

const colourParam: ParamDef = {
  id: 'colour',
  label: 'Colours',
  type: 'float',
  min: 0,
  max: 1,
  default: 0,
  step: 1,
  control: 'select',
  options: ['Rainbow by note', 'Palette by note'],
};

/** Colour for a pitch using the colour param at uP[0]. */
const NOTE_COLOUR = /* glsl */ `
vec3 noteCol(float midi) {
  return uP[0] > 0.5 ? palette(fract(midi / 12.0)) : noteColour(midi);
}
`;

export const melodyRibbon: VisualizationDef = {
  id: 'melody-ribbon',
  name: 'Melody Ribbon',
  description:
    'The melody drawn as it happens: the last few seconds scroll past, higher notes higher, each note in its own colour. The line thickens when the pitch is clear.',
  tags: ['energy:mid', 'style:melody'],
  role: 'element',
  params: [
    colourParam,
    { id: 'low', label: 'Lowest note', type: 'float', min: 40, max: 72, default: 52, step: 1 },
    { id: 'range', label: 'Octaves shown', type: 'float', min: 1, max: 5, default: 3 },
    { id: 'width', label: 'Line width', type: 'float', min: 0.3, max: 3, default: 1 },
    { id: 'grid', label: 'Note lines', type: 'float', min: 0, max: 1, default: 0.5 },
  ],
  fragment: /* glsl */ `
${NOTE_COLOUR}
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float lo = uP[1], span = uP[2] * 12.0;
  float note = lo + uv.y * span;              // the pitch at this height
  vec3 col = vec3(0.0);
  // Faint lines for every C (and every note, fainter), tinted by note.
  float semi = abs(fract(note + 0.5) - 0.5);
  float isC = step(abs(mod(floor(note + 0.5), 12.0)), 0.5);
  col += noteCol(floor(note + 0.5)) * smoothstep(0.06, 0.0, semi) * (0.04 + 0.12 * isC) * uP[4];
  // The trail: newest at the right edge, oldest at the left.
  float x = (1.0 - uv.x) * 127.0;
  int i0 = int(floor(x));
  float f = fract(x);
  vec2 a = uPitchHist[min(i0, 127)];
  vec2 b = uPitchHist[min(i0 + 1, 127)];
  // Don't draw a slope between two different notes: step at the midpoint instead.
  bool jump = abs(a.x - b.x) > 0.75;
  vec2 s = jump ? (f < 0.5 ? a : b) : mix(a, b, f);
  if (s.y > 0.02 && s.x > 0.0) {
    float d = abs(note - s.x) / span * uResolution.y;      // pixels from the line
    float w = (2.0 + 6.0 * s.y) * uP[3];
    float fade = 0.35 + 0.65 * (1.0 - x / 127.0);   // older = dimmer
    vec3 c = noteCol(s.x);
    col += c * (smoothstep(w, w * 0.4, d) * 1.4 + 0.5 * exp(-d / (w * 3.0))) * s.y * fade;
  }
  // The newest note: a glowing head at the right edge.
  vec2 head = uPitchHist[0];
  if (head.y > 0.02) {
    vec2 hp = vec2(uResolution.x * 0.995, (head.x - lo) / span * uResolution.y);
    float d = length(gl_FragCoord.xy - hp);
    col += noteCol(head.x) * exp(-d / (10.0 + 30.0 * pulse())) * head.y * 1.5;
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const noteRings: VisualizationDef = {
  id: 'note-rings',
  name: 'Note Rings',
  description:
    'Every note of the melody becomes a ring of its colour, rippling outward, so the screen fills with the colours of the tune.',
  tags: ['energy:mid', 'style:melody'],
  role: 'background',
  params: [
    colourParam,
    { id: 'spacing', label: 'Spread', type: 'float', min: 0.3, max: 2, default: 1 },
    { id: 'glow', label: 'Glow', type: 'float', min: 0.2, max: 2, default: 1 },
    { id: 'chord', label: 'Chord wash', type: 'float', min: 0, max: 1, default: 0.35 },
  ],
  fragment: /* glsl */ `
${NOTE_COLOUR}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p);
  // Background: the colours of the notes sounding now, mixed.
  vec3 chord = vec3(0.0);
  float tot = 0.0;
  for (int i = 0; i < 12; i++) { chord += noteCol(float(i)) * uChroma[i]; tot += uChroma[i]; }
  chord /= max(tot, 1e-3);
  vec3 col = chord * uP[3] * 0.25 * smoothstep(1.2, 0.0, r) * min(tot, 3.0) / 3.0;
  // Rings: history sample i sits at a radius proportional to its age.
  float x = r / (0.85 * uP[1]) * 127.0;
  if (x < 127.0) {
    int i0 = int(floor(x));
    vec2 a = uPitchHist[i0];
    vec2 b = uPitchHist[min(i0 + 1, 127)];
    float f = fract(x);
    vec2 s = abs(a.x - b.x) > 0.75 ? (f < 0.5 ? a : b) : mix(a, b, f);
    float age = x / 127.0;
    float ripple = 0.75 + 0.25 * sin(r * 90.0 - uTime * 6.0);
    col += noteCol(s.x) * s.y * (1.0 - age) * ripple * uP[2] * 1.2;
  }
  // The current note glows at the centre.
  col += noteCol(uPitch) * uPitchConf * exp(-r * (9.0 - 4.0 * pulse())) * uP[2];
  fragColor = vec4(col, 1.0);
}`,
};

export const chromaWheel: VisualizationDef = {
  id: 'chroma-wheel',
  name: 'Chroma Wheel',
  description:
    'The 12 notes around a circle, each segment growing with how much of that note is sounding, with a needle on the melody note. Can be laid out as the circle of fifths.',
  tags: ['energy:mid', 'style:melody'],
  role: 'element',
  params: [
    colourParam,
    {
      id: 'order',
      label: 'Order',
      type: 'float',
      min: 0,
      max: 1,
      default: 0,
      step: 1,
      control: 'select',
      options: ['Chromatic (C C♯ D …)', 'Circle of fifths (C G D …)'],
    },
    { id: 'size', label: 'Size', type: 'float', min: 0.5, max: 1.6, default: 1 },
    { id: 'spin', label: 'Spin', type: 'float', min: -1, max: 1, default: 0 },
  ],
  fragment: /* glsl */ `
${NOTE_COLOUR}
// Position (0..11) of a pitch class round the wheel.
float slot(float pc) { return uP[1] > 0.5 ? mod(pc * 7.0, 12.0) : pc; }
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y / uP[2];
  float r = length(p);
  // Angle clockwise from the top, in slots.
  float a = atan(p.x, p.y) / TAU + uTime * 0.02 * uP[3];
  float sl = fract(a) * 12.0;
  float s = floor(sl);
  // Which pitch class sits in this slot.
  float pc = uP[1] > 0.5 ? mod(s * 7.0, 12.0) : s;
  float amount = uChroma[int(pc)];
  float inner = 0.12, outer = inner + 0.06 + 0.3 * amount;
  float within = smoothstep(0.02, 0.06, fract(sl)) * smoothstep(0.98, 0.94, fract(sl));
  float seg = step(inner, r) * smoothstep(outer + 0.004, outer - 0.004, r) * within;
  vec3 col = noteCol(pc) * seg * (0.25 + 1.1 * amount);
  // Rim ticks and a soft glow per note.
  col += noteCol(pc) * within * exp(-abs(r - 0.47) * 120.0) * 0.4;
  col += noteCol(pc) * amount * amount * exp(-abs(r - outer) * 40.0) * 0.5 * within;
  // The melody needle.
  if (uPitchConf > 0.05) {
    float target = (slot(mod(uPitch, 12.0)) + 0.5) / 12.0 - uTime * 0.02 * uP[3];
    vec2 dir = vec2(sin(target * TAU), cos(target * TAU));
    float along = dot(p, dir);
    float across = abs(p.x * dir.y - p.y * dir.x);
    float needle = smoothstep(0.006, 0.0, across) * step(0.0, along) * step(along, 0.5);
    col += noteCol(uPitch) * needle * uPitchConf * 1.6;
    col += noteCol(uPitch) * exp(-length(p - dir * 0.52) * 60.0) * uPitchConf * 1.5;
  }
  col += noteCol(uPitch) * exp(-r * 30.0) * (0.3 + pulse()) * uPitchConf;
  fragColor = vec4(col, 1.0);
}`,
};

export const chordColours: VisualizationDef = {
  id: 'chord-colours',
  name: 'Chord Colours',
  description:
    'A drifting wash that takes on the colours of the notes being played: every chord has its own blend, and the melody note shines through.',
  tags: ['energy:low', 'style:melody'],
  role: 'background',
  params: [
    colourParam,
    { id: 'flow', label: 'Flow', type: 'float', min: 0, max: 2, default: 0.6 },
    { id: 'melody', label: 'Melody glow', type: 'float', min: 0, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
${NOTE_COLOUR}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float t = uTime * 0.08 * uP[1];
  vec3 col = vec3(0.0);
  float tot = 0.0;
  // Each sounding note owns a drifting blob of its colour.
  for (int i = 0; i < 12; i++) {
    float fi = float(i);
    vec2 c = 0.5 * vec2(sin(t * (1.0 + 0.13 * fi) + fi * 2.4), cos(t * (0.8 + 0.11 * fi) + fi * 1.7));
    c.x *= uResolution.x / uResolution.y;
    float w = uChroma[i];
    float d = length(p - c);
    col += noteCol(fi) * w * exp(-d * d * 3.0);
    tot += w * exp(-d * d * 3.0);
  }
  col = col / max(tot, 0.15) * min(tot * 1.5, 1.0);
  // The melody note as a soft light that moves with the pitch.
  vec2 mp = vec2((fract(uPitch / 12.0) - 0.5) * 1.2, (uPitch - 66.0) / 40.0);
  col += noteCol(uPitch) * exp(-length(p - mp) * 5.0) * uPitchConf * uP[2] * (0.6 + 0.6 * pulse());
  fragColor = vec4(col * (0.75 + 0.35 * uLevel), 1.0);
}`,
};

export const MELODY_VISUALIZATIONS: VisualizationDef[] = [
  melodyRibbon,
  noteRings,
  chromaWheel,
  chordColours,
];
